import { computed, inject, Injectable, signal } from "@angular/core";
import { RoleService } from "@app/shared/services/role.service";
import {
  FeatureFlagKey,
  FeatureFlagService,
} from "@app/core/services/feature-flag.service";

/**
 * The channels the VDO Shelf UI owns. `VDO` itself is deliberately absent:
 * it is the merged read-only aggregate, never a Shelf dropdown entry, and it
 * renders the common Shelf UI like any other channel.
 *
 * AIS Play was added 2026-07 (change-of-plans, VDOSTORE ranking behaviour
 * promoted onto it — see vdo-shelf.constants.ts / vdo-shelf.component.ts
 * `mode()`). It used to be a synthetic aggregate that rendered the common
 * Shelf UI with no CRUD of its own; it is now a real ranking channel, so it
 * has to resolve `isVdo() === true` to route into this module at all.
 *
 * Three spellings are listed for it because the BFF's own `vdoChannels` set
 * (BE_CMS_plan.md:55) recognises "aisplay", "ais_play" and "ais play" — the
 * FE has to match whichever one the RBAC master data actually hands back, or
 * one spelling would silently fall through to the common Shelf UI instead.
 */
const VDO_CHANNELS = new Set([
  "vdoaccount",
  "vdostore",
  "aisplay",
  "ais_play",
  "ais play",
]);

/**
 * The feature flag that gates the VDO Shelf UI.
 *
 * Was a hardcoded `VDO_SHELF_ENABLED = true` constant. It is now read from
 * `ui-app-config.json` per environment, because VDO must not reach PROD
 * before it ships (suwannapong.i, 2026-08-25: anything not going up yet is
 * wrapped in a featureFlag; a feature riding an existing menu is wrapped
 * inside the function rather than at the menu).
 *
 * Its behaviour is unchanged otherwise: off reverts every VDO channel to the
 * common Shelf UI without touching a route, a host, or the feature module —
 * which is exactly the `if (flag) { new } else { old }` shape the same
 * instruction asks for, with no duplicated branch to maintain.
 *
 * Only VDOACCOUNT, VDOSTORE and AIS Play are affected either way — no other
 * channel can reach the VDO branch, whatever this is set to, because
 * `isVdo()` ANDs the flag with VDO channel membership.
 */
export const VDO_SHELF_FLAG = "enableVdo" satisfies FeatureFlagKey;

/** The feature flag that gates the VDO Campaign menu and route. */
export const VDO_CAMPAIGN_FLAG = "enableVdoCampaign" satisfies FeatureFlagKey;

/**
 * Resolves whether the current request is operating in a VDO channel.
 *
 * Resolution order — this list is precedence order, matching the `??` chain
 * in `channel` below top to bottom; it is NOT the order these values become
 * available, which is closer to the reverse (the RBAC pin is known first, a
 * runtime pick happens last, if ever):
 *   1. `setChannel()` — fed by the tree component's `channelChange` output,
 *      so an ApplicationOwner flipping the dropdown re-renders the host. The
 *      most recent explicit choice always wins over anything computed.
 *   2. `history.state.channelId` — set by `router.navigate(..., { state })`,
 *      which is how the category pages already hand the channel to the
 *      create/edit routes.
 *   3. `roleService.activeChannel()` — the channel the operator actually
 *      ACTIVATED (`applyRoleChannel()`), i.e. what the navbar shows as
 *      current. A role can be pinned to several channels
 *      (`channels = [myAIS, VDOACCOUNT]`); the FIRST one in that list is not
 *      necessarily the one in use, so this must be tried before falling back
 *      to it. Getting this wrong means an operator who activates VDOACCOUNT
 *      lands back on whichever channel happens to be first in their profile.
 *   4. The RBAC pin's first channel — `roleService.selectedRoleProfile()
 *      ?.channels?.[0]`. Only reached when nothing has been activated yet (or
 *      the role is otherwise channel-agnostic); a VDO-only operator cannot
 *      switch channel at all (the dropdown is disabled unless they are an
 *      ApplicationOwner), so for them this is the final answer.
 *
 * (VDO_Shelf_Form_Design.md section 7.0.)
 */
@Injectable()
export class VdoChannelResolver {
  private readonly roleService = inject(RoleService);
  private readonly flags = inject(FeatureFlagService);

  /** Channel selected at runtime, e.g. by an admin using the dropdown. */
  private readonly selected = signal<string | null>(null);

  readonly channel = computed(
    () =>
      this.selected() ??
      this.channelFromHistoryState() ??
      this.roleService.activeChannel()?.channelName ??
      this.roleService.selectedRoleProfile()?.channels?.[0]?.channelName ??
      null,
  );

  /**
   * ⚠️ FAMILY, not `isVdoChannel` (Gus, 2026-09-01: "they should be able to use
   * any and still be able to access the others").
   *
   * This used to exclude the bare `VDO` aggregate, so an operator pinned to it
   * was routed to the COMMON Shelf page — whose channel dropdown is gated on
   * `isApplicationOwner()` in a byte-frozen component — and could reach no VDO
   * shelf at all. Widening the VDO page's own dropdown could never help them,
   * because they never landed on it.
   *
   * The aggregate is still not a Shelf dropdown OPTION and still not a writable
   * channel: `isVdoChannel` keeps rejecting it, `VDO_FALLBACK` never lists it,
   * and `vdoMode()` resolves it to VDOACCOUNT. This changes only WHICH PAGE an
   * aggregate-bound operator lands on.
   *
   * The isolation contract is unaffected — `isVdoFamilyChannel` accepts exactly
   * the three real VDO channels plus the aggregate, and nothing else.
   */
  readonly isVdo = computed(
    () => this.flags.get(VDO_SHELF_FLAG) && isVdoFamilyChannel(this.channel()),
  );

  setChannel(channel: string | null): void {
    this.selected.set(channel);
  }

  private channelFromHistoryState(): string | null {
    // `history` is unavailable during SSR, and this component tree is
    // server-rendered, so guard rather than assume a browser.
    if (typeof history === "undefined") return null;
    const state = history.state as { channelId?: unknown } | null;
    return typeof state?.channelId === "string" ? state.channelId : null;
  }
}

/** Case-insensitive membership test against the VDO channel set. */
export function isVdoChannel(channel: string | null | undefined): boolean {
  if (!channel) return false;
  return VDO_CHANNELS.has(channel.trim().toLowerCase());
}

/** True for the plain aggregate `VDO` channel binding — deliberately NOT a
 *  member of `VDO_CHANNELS` above (`isVdoChannel("VDO")` stays `false`: the
 *  bare aggregate must keep rendering the common Shelf UI, never the VDO
 *  module). Exported separately for callers that need to recognise a caller
 *  BOUND to the aggregate itself, distinct from routing into VDO Shelf. */
export function isVdoAggregateChannel(
  channel: string | null | undefined,
): boolean {
  if (!channel) return false;
  return channel.trim().toLowerCase() === "vdo";
}

/** True for ANY VDO-family binding: one of the three real VDO channels
 *  (`isVdoChannel`) OR the plain aggregate (`isVdoAggregateChannel`) above.
 *
 * Added for the 2026-08-07 demo feedback (issues 13-15): "VDO user should
 * simulate to have one role (plain VDO) and is able to view all VDO-family
 * channels". Used by `VdoChannelOptionsService` to decide whether a
 * channel-pinned operator's Shelf dropdown should widen to all three real
 * VDO channels — a deliberately BROADER test than `isVdoChannel`, whose job
 * is routing into the VDO Shelf module, not deciding who gets to see every
 * channel in a dropdown. The aggregate itself must never become a Shelf
 * option even when this returns `true` for it — see
 * `vdo-channel-options.service.ts`. */
export function isVdoFamilyChannel(
  channel: string | null | undefined,
): boolean {
  return isVdoChannel(channel) || isVdoAggregateChannel(channel);
}

/**
 * Canonicalises a raw channel string to the VDO Shelf mode it maps to, or
 * `null` when it is not (a spelling of) a VDO channel at all.
 *
 * Case- and spelling-insensitive, reusing the SAME `VDO_CHANNELS` membership
 * this module already keeps: pinned role channels and master-channel rows
 * carry the OLD mixed-case spellings ("VDOAccount" / "VDOStore"), and the
 * BFF's own `vdoChannels` set recognises three spellings for AIS Play
 * ("aisplay", "ais_play", "ais play"). Callers that need to decide WHICH of
 * the three VDO modes a raw channel string is — not just whether it is one —
 * must go through this rather than hand-rolling a second case fold, or a
 * fresh spelling variant only gets fixed in one of the two places.
 */
export function canonicalVdoMode(
  channel: string | null | undefined,
): "VDOACCOUNT" | "VDOSTORE" | "AIS Play" | null {
  if (!isVdoChannel(channel)) return null;
  const key = (channel as string).trim().toLowerCase();
  if (key === "vdoaccount") return "VDOACCOUNT";
  if (key === "vdostore") return "VDOSTORE";
  // The only other member of VDO_CHANNELS is one of the AIS Play spellings.
  return "AIS Play";
}
